/* Awards — bộ sưu tập badge; chạm vào badge để lật xem điều kiện đạt. */
import { useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Icon } from "@/components/icon"
import { EmptyState, Meter, Page, PageHeader, usePageTitle } from "@/components/page"
import { BadgeMedallion, EditorialVisual } from "@/components/simba"
import { useClass } from "@/app/ClassLayout"
import { formatDisplayDate } from "@/lib/format"
import { cn } from "@/lib/utils"

const FACE = "col-start-1 row-start-1 flex flex-col items-center gap-2 rounded-[20px] border-[1.5px] p-5 text-center backface-hidden"

function BadgeCard({ sticker }: { sticker: any }) {
  const [flipped, setFlipped] = useState(false)
  const earned = !!Number(sticker.earned)
  const condition = sticker.description || sticker.reason || "Hoàn thành thử thách của badge này."
  return (
    <button
      type="button"
      data-interaction="flip-badge"
      aria-label={`${sticker.name}. ${flipped ? "Quay lại mặt trước" : "Xem điều kiện đạt badge"}`}
      aria-pressed={flipped}
      onClick={() => setFlipped((f) => !f)}
      className="group perspective-[1000px] text-left"
    >
      <span className={cn("grid h-full min-h-64 transition-transform duration-500 transform-3d", flipped && "rotate-y-180")}>
        <span className={cn(FACE, "justify-center shadow-card", earned ? "border-edge bg-card" : "border-dashed border-edge bg-card/60")}>
          <span className="relative">
            <BadgeMedallion name={sticker.name} description={sticker.description} earned={earned} />
            {!earned && (
              <span className="absolute right-0 bottom-2 grid size-8 place-items-center rounded-full bg-ink text-white">
                <Icon name="lock" className="size-4" />
              </span>
            )}
          </span>
          {!earned && <Badge variant="secondary">Chưa mở khóa</Badge>}
          <strong className="font-heading text-base font-semibold text-ink">{sticker.name}</strong>
          {earned && sticker.awarded_at && <time className="text-sm text-success">Nhận ngày {formatDisplayDate(sticker.awarded_at)}</time>}
          <span className="text-sm text-muted-foreground group-hover:text-orange-strong">Chạm để xem điều kiện</span>
        </span>
        <span className={cn(FACE, "rotate-y-180 justify-center border-orange/40 bg-paper shadow-card")}>
          <span className="text-sm font-bold tracking-[0.06em] text-orange uppercase">Điều kiện để đạt badge</span>
          <strong className="font-heading text-lg font-semibold text-ink">{sticker.name}</strong>
          <p className="text-sm text-ink">{condition}</p>
          <span className="text-sm text-muted-foreground">Chạm để quay lại</span>
        </span>
      </span>
    </button>
  )
}

export default function RewardsPage() {
  const { data } = useClass()
  const stickers: any[] = data.stickers || []
  const earned = stickers.filter((s) => Number(s.earned)).length
  usePageTitle("Awards", "Bộ sưu tập badge của con trong lớp học.")

  return (
    <Page>
      <PageHeader eyebrow="Bộ sưu tập" title="Awards" description={stickers.length ? `Con đã mở khóa ${earned}/${stickers.length} badge.` : undefined} />
      <EditorialVisual kind="community" alt="Một bộ sưu tập thành quả học tập trên hai hành trình đồng hành" />
      {stickers.length > 0 && <Meter value={(earned / stickers.length) * 100} />}
      {stickers.length ? (
        <div className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {stickers.map((s) => (
            <BadgeCard key={s.id} sticker={s} />
          ))}
        </div>
      ) : (
        <EmptyState icon="award" title="Chưa có badge" />
      )}
    </Page>
  )
}
